/* @flow */
import React, { useEffect, useRef, useCallback } from 'react';
import {
    Circle as VisGlCircle,
    Polygon as VisGlPolygon,
    Rectangle as VisGlRectangle,
    Polyline as VisGlPolyline,
} from '@vis.gl/react-google-maps';

/**
 * Bridges a caller-supplied ref (object ref or callback ref) and our own local
 * ref, and attaches the `rightclick` listener that vis.gl does not expose.
 */
const useShapeRef = (forwardedRef: any, onRightClick: any) => {
    const instanceRef = useRef(null);
    const listenerRef = useRef(null);
    const [instance, setInstance] = React.useState(null);

    const setRef = useCallback((inst) => {
        instanceRef.current = inst;
        setInstance(inst);
        if (typeof forwardedRef === 'function') forwardedRef(inst);
        else if (forwardedRef && typeof forwardedRef === 'object') {
            // eslint-disable-next-line no-param-reassign
            forwardedRef.current = inst;
        }
    }, [forwardedRef]);

    useEffect(() => {
        if (listenerRef.current) {
            listenerRef.current.remove();
            listenerRef.current = null;
        }
        if (instance && onRightClick && window.google?.maps) {
            listenerRef.current = window.google.maps.event.addListener(instance, 'rightclick', onRightClick);
        }
        return () => {
            if (listenerRef.current) {
                listenerRef.current.remove();
                listenerRef.current = null;
            }
        };
    }, [instance, onRightClick]);

    return setRef;
};

const toBoundsLiteral = (bounds: any) => {
    if (!bounds) return bounds;
    if (typeof bounds.toJSON === 'function') return bounds.toJSON();
    return bounds;
};

export const Circle = React.forwardRef((props: Object, ref: any) => {
    const { options = {}, onRightClick, ...rest } = props;
    const setRef = useShapeRef(ref, onRightClick);
    return <VisGlCircle ref={setRef} {...options} {...rest} />;
});

export const Polygon = React.forwardRef((props: Object, ref: any) => {
    const { options = {}, onRightClick, ...rest } = props;
    const setRef = useShapeRef(ref, onRightClick);
    return <VisGlPolygon ref={setRef} {...options} {...rest} />;
});

export const Rectangle = React.forwardRef((props: Object, ref: any) => {
    const { options = {}, onRightClick, bounds, defaultBounds, ...rest } = props;
    const setRef = useShapeRef(ref, onRightClick);
    return (
        <VisGlRectangle
            ref={setRef}
            bounds={toBoundsLiteral(bounds)}
            defaultBounds={toBoundsLiteral(defaultBounds)}
            {...options}
            {...rest}
        />
    );
});

export const Polyline = React.forwardRef((props: Object, ref: any) => {
    const { options = {}, onRightClick, ...rest } = props;
    const setRef = useShapeRef(ref, onRightClick);
    return <VisGlPolyline ref={setRef} {...options} {...rest} />;
});
